import React, { useMemo, useState } from 'react';
import { FormattedMessage, defineMessages, useIntl } from 'react-intl';
import { navigate } from 'vike/client/router';
import { BellRinging, BellSimple } from '@phosphor-icons/react';
import { useFollowedCompanies } from '../../hooks/useFollowedCompanies';
import { useCompanyFollowActions } from '../../hooks/useCompanyFollowActions';
import { useAuthOptional } from '../../contexts/AuthContext';
import { useToast } from '../../contexts/ToastContext';
import { normalizeSupportedLocale } from '../../utils/routing/locale-from-pathname';
import { localizeRoute } from '../../utils/constants/url-constants';
import { trackEvent } from '../../utils/analytics/analytics';
import { subscribeToMasterAlerts } from '../../utils/email-alerts/subscribe-to-master-alerts';
import { hasSeenFollowEmailPrompt, markFollowEmailPromptSeen } from '../../utils/engagement/follow-email-prompt';
import { Msg, useFormatMessage } from '../../i18n-review/client/Msg';

const promptMessages = defineMessages({
  offer: {
    id: 'company.follow.emailPrompt.offer',
    defaultMessage: 'Following {company} — want an email when they post new roles?',
    description: 'Toast offering to turn on email updates right after following a company',
  },
  turnOn: {
    id: 'company.follow.emailPrompt.turnOn',
    defaultMessage: 'Turn on',
    description: 'Toast action that enables email updates for followed companies',
  },
  enabled: {
    id: 'company.follow.emailPrompt.enabled',
    defaultMessage: "You're set — new roles from companies you follow will be in your job-alert emails.",
    description: 'Toast confirming email updates were enabled from the follow prompt',
  },
  pendingConfirmation: {
    id: 'company.follow.emailPrompt.pendingConfirmation',
    defaultMessage: 'Almost there — confirm your email in your inbox to activate updates.',
    description: 'Toast when enabling email updates still needs the account email confirmed',
  },
  failed: {
    id: 'company.follow.emailPrompt.failed',
    defaultMessage: "Couldn't turn on email updates — you can enable them any time from the Alerts page.",
    description: 'Toast when enabling email updates from the follow prompt fails',
  },
});

export interface CompanyFollowButtonProps {
  companyId: string;
  companyName?: string;
  /** Use the light "on-brand" treatment when placed over a colored BrandPanel. */
  onBrand?: boolean;
  /** Icon-only circular button, for tight spaces like a listing row/card corner. */
  compact?: boolean;
  /** Compact button diameter in px (and a proportionally-scaled icon) — default 32, matching the
   *  SRP hit-card corner badge. Pass 44 to match job-detail's other round action buttons
   *  (JD_ICON_BTN_BASE in pages/job/@jobId/@seoSlug/utils/job-detail-action-chrome.ts). */
  compactSize?: number;
  /** Where this button is placed (job-page, srp-hit, company-profile, ...) — recorded on the analytics event. */
  surface?: string;
  /** Called after a click, before the follow/unfollow request resolves — use to stop the click reaching a wrapping Link/card. */
  onClick?: (event: React.MouseEvent<HTMLButtonElement>) => void;
}

/**
 * Follow/unfollow entry point for the Watchlist (SEEKER_HUB_PLAN.md Phase 4). A followed
 * company is a live-data subscription (open roles, employer score, trend), not a bookmark.
 */
export const CompanyFollowButton: React.FC<CompanyFollowButtonProps> = ({ companyId, companyName, onBrand = false, compact = false, compactSize = 32, surface, onClick }) => {
  const intl = useIntl();
  const formatMessage = useFormatMessage();
  const { showToast } = useToast();
  const auth = useAuthOptional();
  const followedCompanies = useFollowedCompanies();
  const { followCompany, unfollowCompany } = useCompanyFollowActions();
  const [busy, setBusy] = useState(false);

  const isFollowing = useMemo(
    () => followedCompanies.some((c) => c.companyId === companyId),
    [followedCompanies, companyId]
  );

  const handleEnableEmailUpdates = async () => {
    const locale = normalizeSupportedLocale(intl.locale);
    trackEvent({ name: 'follow_email_prompt', params: { action: 'accepted', company_id: companyId, surface: surface ?? 'unknown', locale: intl.locale } });
    try {
      await subscribeToMasterAlerts(locale);
      // Distinguish "on" from "waiting on email confirmation" — the subscribe
      // endpoint puts unverified accounts into pendingConfirmation.
      let status: string | undefined;
      try {
        const res = await fetch('/api/alerts/subscription');
        if (res.ok) status = (await res.json())?.subscription?.status;
      } catch {
        // Status check is cosmetic; fall through to the generic confirmation.
      }
      showToast(
        formatMessage(status === 'pendingConfirmation' ? promptMessages.pendingConfirmation : promptMessages.enabled),
        'success',
        5000
      );
    } catch {
      showToast(formatMessage(promptMessages.failed), 'error', 5000);
    }
  };

  /**
   * The activation wedge: right after a first follow, signed-in users without
   * active alert emails are offered them once (per browser) — a follow is only
   * a live subscription if its updates can actually reach the user.
   * Returns true when the offer toast was shown (it doubles as the follow
   * confirmation, so the caller must skip the standard toast).
   */
  const maybeOfferEmailUpdates = async (): Promise<boolean> => {
    if (!auth?.isAuthenticated || hasSeenFollowEmailPrompt()) return false;
    try {
      const res = await fetch('/api/alerts/subscription');
      if (!res.ok) return false;
      const status = (await res.json())?.subscription?.status;
      if (status === 'active') return false;
    } catch {
      return false;
    }
    markFollowEmailPromptSeen();
    trackEvent({ name: 'follow_email_prompt', params: { action: 'shown', company_id: companyId, surface: surface ?? 'unknown', locale: intl.locale } });
    showToast(
      formatMessage(promptMessages.offer, { company: companyName ?? companyId }),
      'success',
      8000,
      {
        label: formatMessage(promptMessages.turnOn),
        onClick: () => void handleEnableEmailUpdates(),
      }
    );
    return true;
  };

  const handleClick = async (event: React.MouseEvent<HTMLButtonElement>) => {
    onClick?.(event);
    setBusy(true);
    try {
      if (isFollowing) {
        trackEvent({ name: 'company_unfollow', params: { company_id: companyId, surface: surface ?? 'unknown', locale: intl.locale } });
        const ok = await unfollowCompany(companyId);
        if (ok) {
          showToast(
            formatMessage({ id: 'company.follow.unfollowedToast', defaultMessage: 'Unfollowed', description: 'Toast after unfollowing a company' }),
            'info'
          );
        }
      } else {
        trackEvent({ name: 'company_follow', params: { company_id: companyId, surface: surface ?? 'unknown', locale: intl.locale } });
        const ok = await followCompany(companyId, { companyName });
        if (ok) {
          const offered = await maybeOfferEmailUpdates();
          if (!offered) {
            const locale = normalizeSupportedLocale(intl.locale);
            showToast(
              formatMessage({ id: 'company.follow.followedToast', defaultMessage: 'Following — watchlist updated', description: 'Toast after following a company' }),
              'success',
              3000,
              {
                label: formatMessage({ id: 'company.follow.viewWatchlist', defaultMessage: 'View →', description: 'Toast action link into the Watchlist' }),
                onClick: () => navigate(`/${locale}${localizeRoute('/tools/watchlist', locale)}`),
              }
            );
          }
        }
      }
    } finally {
      setBusy(false);
    }
  };

  const baseClass = onBrand
    ? 'border-white/40 bg-white/15 text-white hover:bg-white/25'
    : 'border-jc-border bg-jc-surface text-jc-ink-secondary hover:bg-jc-elevated hover:text-jc-ink';
  const activeClass = onBrand
    // Fixed dark text, not the theme-reactive jc-ink token: this pill sits on the brand-color
    // panel regardless of site theme, and jc-ink flips to near-white in dark mode — text-jc-ink
    // here would go invisible (white text on the white active background).
    ? 'border-white bg-white text-[#141413] hover:bg-white/90'
    : 'border-jc-accent bg-jc-accent-muted text-jc-accent hover:bg-jc-accent-muted/70';

  const followLabel = formatMessage({ id: 'company.follow.cta', defaultMessage: 'Follow', description: 'Follow button label to start following a company' });
  const followingLabel = formatMessage({ id: 'company.follow.following', defaultMessage: 'Following', description: 'Follow button label when already following a company' });

  if (compact) {
    const iconSize = Math.round(compactSize * 0.5);
    return (
      <button
        type="button"
        onClick={handleClick}
        disabled={busy}
        aria-label={isFollowing ? followingLabel : followLabel}
        aria-pressed={isFollowing}
        title={isFollowing ? followingLabel : followLabel}
        className={`inline-flex flex-shrink-0 items-center justify-center rounded-full border transition-colors disabled:opacity-60 ${isFollowing ? activeClass : baseClass}`}
        style={{ width: compactSize, height: compactSize }}
      >
        {isFollowing ? (
          <BellRinging style={{ width: iconSize, height: iconSize }} weight="fill" aria-hidden />
        ) : (
          <BellSimple style={{ width: iconSize, height: iconSize }} weight="bold" aria-hidden />
        )}
      </button>
    );
  }

  return (
    <button
      type="button"
      onClick={handleClick}
      disabled={busy}
      className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-sm font-semibold transition-colors disabled:opacity-60 ${isFollowing ? activeClass : baseClass}`}
    >
      {isFollowing ? <BellRinging className="w-4 h-4" weight="fill" aria-hidden /> : <BellSimple className="w-4 h-4" weight="bold" aria-hidden />}
      {isFollowing ? (
        <Msg id="company.follow.following" defaultMessage="Following" description="Follow button label when already following a company" />
      ) : (
        <Msg id="company.follow.cta" defaultMessage="Follow" description="Follow button label to start following a company" />
      )}
    </button>
  );
};
